import { useRef } from 'react';
import { toast } from '../../func.js';
import api from '../../common/api/index.ts';
import endpoints from '../../common/endpoints.ts';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import NewFileUploader from '../../utilities/newFileUploader.ts';

const MAX_FILE_SIZE = 50 * 1024 * 1024;

const SopUpload = () => {
  const abortControllerRef = useRef(null);
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();

  const sopAttachmentsQueryKey = [
    endpoints.file.read.many.sopAttachments.by.customerId(),
  ];

  const cancelOngoingRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const createAbortSignal = () => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return controller.signal;
  };

  const handleToast = errOrMessage => {
    if (typeof errOrMessage === 'string') {
      toast(errOrMessage);
      return;
    }

    if (errOrMessage?.response?.data) {
      toast(errOrMessage.response.data);
    } else {
      console.log(errOrMessage);
    }
  };

  const { data: uploadedFiles, isLoading } = useQuery({
    queryKey: sopAttachmentsQueryKey,
    queryFn: api.file.read.many.sopAttachments.by.customerId,
    enabled: true,
  });

  const downloadTemplateMutation = useMutation({
    mutationFn: async () => {
      cancelOngoingRequest();
      const signal = createAbortSignal();

      const arrayBuffer = await api.file.download.sop(signal);

      const contentType =
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const fileName = 'DSCSA_SOP_Template.docx';

      const blob = new Blob([arrayBuffer], { type: contentType });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    },
    onError: err => {
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
      console.error(err);
      handleToast(err);
    },
  });

  const downloadFileMutation = useMutation({
    mutationFn: async file => {
      cancelOngoingRequest();
      const signal = createAbortSignal();

      const downloadBuffer = await api.file.download.sopAttachment.by.id(
        file.id,
        signal
      );

      const contentType = file.contentType;
      const fileName = file.filename;

      const blob = new Blob([downloadBuffer], { type: contentType });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    },
    onError: err => {
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
      console.error(err);
      handleToast(err);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async file => {
      cancelOngoingRequest();
      const signal = createAbortSignal();

      await api.file.delete.one.sopAttachment.by.id(file.id, signal);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sopAttachmentsQueryKey });
    },
    onError: err => {
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
      console.error(err);
      handleToast(err);
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async files => {
      const uploader = new NewFileUploader(files, endpoints.file.upload.sop());

      return await uploader.upload();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sopAttachmentsQueryKey });
    },
    onError: err => {
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
      console.error(err);
      handleToast(err);
    },
  });

  const handleFileChange = async e => {
    const input = e.target;
    const files = Array.from(input.files || []);

    if (files.length === 0) return;

    if (uploadedFiles?.length + files?.length > 10) {
      toast({
        payload: {
          msg: 'The addition of these files exceeds your limit of 10 files',
          class: 'red white-text',
        },
      });
      return (input.value = '');
    }
    const oversizeFileBatch = files.reduce(
      (total, file) => total + file.size,
      0
    );

    if (oversizeFileBatch > MAX_FILE_SIZE) {
      toast({
        payload: {
          msg: `"Total file(s) size exceeds limit (max 50Mb).`,
          class: 'red white-text',
        },
      });
      return (input.value = '');
    }

    const filenames = files.map(file => file.name);
    const existingFile = uploadedFiles.find(uploadedFile =>
      filenames.includes(uploadedFile.filename)
    );
    if (existingFile) {
      toast({
        payload: {
          msg: `${existingFile.filename} has already been uploaded`,
          class: 'red white-text',
        },
      });
      return (input.value = '');
    }

    uploadMutation.mutate(files);

    return (input.value = '');
  };

  const downloadSop = () => {
    downloadTemplateMutation.mutate();
  };

  const downloadFile = file => {
    downloadFileMutation.mutate(file);
  };

  const deleteFile = file => {
    deleteMutation.mutate(file);
  };

  const openUploadBrowse = () => {
    if ((uploadedFiles?.length || 0) > 9) {
      return toast({
        payload: {
          msg: 'You have reached your limit of 10 files',
          class: 'red white-text',
        },
      });
    }

    if (!uploadedFiles?.length) {
      if (
        window.confirm(
          'Would you like to first download the Standard Operating Procedures Template file?'
        )
      ) {
        return downloadSop();
      }
    }

    fileInputRef.current?.click();
  };

  const isBusy =
    isLoading ||
    uploadMutation.isPending ||
    deleteMutation.isPending ||
    downloadTemplateMutation.isPending ||
    downloadFileMutation.isPending;

  return (
    <div className="col" style={{ paddingTop: '20px' }}>
      {isBusy ? (
        <div className="progress">
          <div className="indeterminate" />
        </div>
      ) : (
        <>
          <div
            className="row"
            style={{ margin: '20px', paddingBottom: '20px' }}
          >
            <button
              className="btn-small blue white-text waves-effect waves-light col s3 offset-s2"
              onClick={downloadSop}
            >
              Download SOP Template
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <button
              className="btn-small blue white-text waves-effect waves-light col s3 offset-s2"
              onClick={openUploadBrowse}
            >
              Upload
            </button>
          </div>
          <div>
            <table>
              <thead>
                <tr>
                  <th style={{ padding: '2px' }}>Filename</th>
                  <th style={{ padding: '2px' }}>Uploaded By</th>
                  <th style={{ padding: '2px' }}>Date Uploaded</th>
                  <th style={{ padding: '2px' }}></th>
                  <th style={{ padding: '2px' }}></th>
                </tr>
              </thead>
              <tbody>
                {uploadedFiles?.map(file => (
                  <tr
                    key={file.id}
                    className="hoverable"
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{file.filename}</td>
                    <td>
                      {file.uploadedBy.firstName} {file.uploadedBy.lastName}
                    </td>
                    <td>{file.dateUploaded}</td>
                    <td onClick={() => downloadFile(file)}>
                      <i
                        className="material-icons hoverable"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '10px',
                          height: '30px',
                          width: '30px',
                        }}
                      >
                        cloud_download
                      </i>
                    </td>
                    <td onClick={() => deleteFile(file)}>
                      <i
                        className="material-icons hoverable"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '10px',
                          height: '30px',
                          width: '30px',
                        }}
                      >
                        delete
                      </i>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>{' '}
          </div>
        </>
      )}
    </div>
  );
};

export default SopUpload;
